import React, { useState, useEffect } from 'react';
import { useApi } from '../../apiHelpers';
import { parseDate, formatEventDate, getDisplayStatus } from '../../utils/date-utils';
import dayjs from 'dayjs';
import Panel from '../../components/ui/Panel';
import EmptyState from '../../components/ui/EmptyState';
import { ListStack, ListRow } from '../../components/ui/ListRow';
import { Skeleton, SkeletonGroup } from '../../components/ui/Skeleton';
import FadeIn from '../../components/ui/FadeIn';
import Tag from '../../components/ui/Tag';
import { CATEGORY } from '../../components/ui/categoryColors';

const statusHues = {
  pending: 'neutral',
  confirmed: 'blue',
  packing: 'orange',
  active: 'green',
  completed: 'emerald',
};

const UpcomingEventsModule = () => {
  const api = useApi();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const data = await api.get('/events', { silent: true });
        // Compare on day granularity in UTC — parseDate() returns a UTC
        // start-of-day, so comparing against local dayjs() dropped *today's*
        // shows once local time passed midnight-UTC. Include today onward.
        const today = dayjs.utc().startOf('day');
        const upcoming = (data || [])
          .filter(e => {
            const d = parseDate(e.event_date);
            return d && !d.isBefore(today) && e.status !== 'cancelled';
          })
          .sort((a, b) => parseDate(a.event_date)?.diff(parseDate(b.event_date)))
          .slice(0, 5);
        setEvents(upcoming);
      } catch { /* silent */ }
      setLoading(false);
    };
    fetch();
  }, []);

  if (loading) {
    return (
      <Panel title="Upcoming Events" titleStyle="section" accent="blue" level={3}>
        <SkeletonGroup label="Loading events" className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </SkeletonGroup>
      </Panel>
    );
  }

  if (events.length === 0) {
    return (
      <Panel title="Upcoming Events" titleStyle="section" accent="blue" level={3}>
        <EmptyState variant="area">No upcoming events</EmptyState>
      </Panel>
    );
  }

  return (
    <Panel title="Upcoming Events" titleStyle="section" accent="blue" lit level={3}>
      <FadeIn>
      <ListStack
        label="Upcoming events"
        on="panel"
        items={events}
        getKey={(item) => String(item.id)}
        renderRow={(event) => {
          const date = parseDate(event.event_date);
          const daysUntil = date.diff(dayjs.utc().startOf('day'), 'day');
          const totalDrones = (parseInt(event.ls_drone_count) || 0) + (parseInt(event.pyro_drone_count) || 0);
          const statusHex = CATEGORY[statusHues[getDisplayStatus(event)] || 'neutral'].ink.hex;

          return (
            <ListRow
              to={`/events/${event.id}`}
              leading={
              <span className="block w-12 text-center">
                {daysUntil <= 0 ? (
                  <span className="block text-[13px] font-bold tracking-wide leading-none" style={{ color: CATEGORY.red.ink.oklch }}>Today</span>
                ) : (
                  <>
                    <span className="block text-lg font-bold tabular-nums" style={{ color: (daysUntil <= 3 ? CATEGORY.red : daysUntil <= 7 ? CATEGORY.orange : CATEGORY.blue).ink.oklch }}>
                      {daysUntil}
                    </span>
                    <span className="block text-[10px] text-on-surface-variant">days</span>
                  </>
                )}
              </span>
              }
              title={event.name}
              meta={`${formatEventDate(event.event_date, 'MMM D')}, ${totalDrones} drones`}
              trailing={
                <>
                  <Tag size="sm" hex={statusHex}>{getDisplayStatus(event)}</Tag>
                </>
              }
            />
          );
        }}
      />
      </FadeIn>
    </Panel>
  );
};

export default UpcomingEventsModule;
